import { CheckCircle2, Fingerprint, Hash, HeartHandshake } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { bytes } from "@tether/crypto";
import { useSession } from "../App.js";
import { api } from "../lib/api.js";
import { getOrCreateGroupKey } from "../lib/keys.js";
import { Spinner } from "../lib/Spinner.js";
import { passkeyHostnameProblem, passkeysSupported, registerPasskey } from "../lib/passkeys.js";

interface Info {
  confidant: { id: string; name: string; shareIndex: number; hasUser: boolean };
  vault: { id: string; name: string; k: number; n: number };
  owner: { name: string };
}

export function Enroll() {
  const { token = "" } = useParams();
  const { me, refresh } = useSession();
  const [info, setInfo] = useState<Info | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ fingerprint: string; vaultSealed: boolean } | null>(null);

  useEffect(() => {
    api<Info>(`/v1/enroll/${token}`)
      .then((i) => {
        setInfo(i);
        setName(i.confidant.name);
      })
      .catch((e) => setErr((e as Error).message));
  }, [token]);

  async function createPasskey() {
    setBusy(true);
    setErr(null);
    try {
      await registerPasskey({ name, inviteToken: token });
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function complete() {
    if (!me) return;
    setBusy(true);
    setErr(null);
    try {
      const kp = getOrCreateGroupKey(me.user.id);
      const r = await api<{ fingerprint: string; vaultSealed: boolean }>(
        `/v1/enroll/${token}/complete`,
        {
          body: { code, groupPublicKey: bytes.toBase64Url(kp.publicKey) },
        },
      );
      setDone(r);
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (err && !info) return <p className="error">{err}</p>;
  if (!info) return <Spinner label="Loading invitation…" />;

  const step = done ? 3 : me && me.user.role === "confidant" ? 2 : 1;

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="card">
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <HeartHandshake size={20} className="icon" />
          You've been asked to be a confidant
        </h2>
        <p>
          <strong>{info.owner.name}</strong> is asking you to help protect{" "}
          <strong>{info.vault.name}</strong>. If {info.vault.name} ever goes missing or needs
          evidence, {info.vault.k} of {info.vault.n} confidants must approve before any recording
          can be opened. You would be one of them.
        </p>
        <p className="muted">
          Your passkey signs you in. A separate decryption key is created in this browser to open
          what is sealed to you, and you will be asked to back it up.{" "}
          <Link to="/guide#keys">How the keys work</Link>
        </p>
      </div>
      <div className="steps">
        <div className={step === 1 ? "active" : "done"}>1. Passkey</div>
        <div className={step === 2 ? "active" : step > 2 ? "done" : ""}>2. Code</div>
        <div className={step === 3 ? "active" : ""}>3. Compare numbers</div>
      </div>
      {step === 1 && (
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Fingerprint size={18} className="icon" />
            Create your passkey
          </h2>
          {!passkeysSupported() && <p className="error">This browser does not support passkeys.</p>}
          {passkeyHostnameProblem() && <p className="error">{passkeyHostnameProblem()}</p>}
          {me && me.user.role !== "confidant" && (
            <p className="error">
              You are signed in as the owner. Open this link in a different browser profile.
            </p>
          )}
          <label>Your name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
          {err && <p className="error">{err}</p>}
          <button
            className="primary"
            style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}
            onClick={createPasskey}
            disabled={busy || !name}
          >
            <Fingerprint size={15} />
            {busy ? "Waiting for passkey…" : "Create passkey"}
          </button>
        </div>
      )}
      {step === 2 && (
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Hash size={18} className="icon" />
            Enter the 6-digit code
          </h2>
          <p className="muted">
            {info.owner.name} told you this code in person or by voice. It proves the link was not
            intercepted.
          </p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="123456"
          />
          {err && <p className="error">{err}</p>}
          <button
            className="primary"
            style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}
            onClick={complete}
            disabled={busy || code.length !== 6}
          >
            <Hash size={15} />
            Claim my share
          </button>
        </div>
      )}
      {step === 3 && done && (
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircle2 size={18} className="icon" style={{ color: "var(--ok)" }} />
            Your share is stored
          </h2>
          <p>
            Read this safety number to {info.owner.name}. They will compare it with what they see
            and confirm you. Until then you cannot see {info.vault.name}'s location history.
          </p>
          <div className="fingerprint">{done.fingerprint}</div>
          <p className="muted" style={{ marginTop: 12 }}>
            {done.vaultSealed
              ? "The vault is now sealed."
              : "The vault seals once enough confidants have enrolled."}
          </p>
        </div>
      )}
    </div>
  );
}
